import {
  makeStyles,
  Grid,
} from "@material-ui/core";
import { DsText, size } from "@mvp/design-system";
import React from "react";
import { useAppStore } from "../../../hooks/useAppStore";
import { NA } from "../../../store/constants";
import AnnualDeductibleOrLimit from "./AnnualDeductibleOrLimit";
import RxCoverage from "./RxCoverage";
import { capitalizeWords, formatToDollarAmount } from "../../../utils/string";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  contentCell: {
    padding: `${size.xxxs(theme)} ${size.xxs(theme)}`
  },
  titleCell: {
    padding: `${size.xs(theme)} ${size.xxs(theme)}`,
  },
  tableContainer: {
    background: theme.palette.grey[50],
    borderRadius: size.xxs(theme),
    padding: `.375rem 1.25rem 1.625rem`,
    '@media print': {
      background: theme.palette.grey[50],
      '-webkit-print-color-adjust': 'exact',
      'print-color-adjust': 'exact',
    },
  },
  mobileTableContainer: {
    background: theme.palette.grey[50],
    borderRadius: size.xxs(theme),
    padding: `0 ${size.xxxs(theme)} ${size.xs(theme)}`,
  },
  gridBottomMargin: {
    marginBottom: size.s(theme),
  },
}));

export const Details = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    appStore: { planInfo },
  } = useAppStore();

  return (
    <Grid container className={classes.tableContainer}>
      <Grid container item>
        <Grid item xs={4} className={classes.titleCell}>
          <DsText variant="body2" text={t("planDetails.title1")} />
        </Grid>
        <Grid item className={classes.titleCell}>
          <DsText variant="body2" text={t("planDetails.title2")} />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.state")} />
          <DsText variant="body2" text={capitalizeWords(planInfo?.planState || NA)} />
          <DsText variant="caption" text={capitalizeWords(planInfo?.planCounty || NA)} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t('planDetails.innDeductible')}
            annualDeductibleOrLimitValue={
              planInfo?.annualInnDeductible || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.primaryCare")} />
          <DsText variant="body2" text={formatToDollarAmount(planInfo?.primaryCareVisit || NA)} />
          <DsText
            variant="caption"
            text={planInfo?.primaryCareVisitLe || NA}
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.planType")} />
          <DsText variant="body2" text={planInfo?.planType || NA} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t("planDetails.oonDeductible")}
            annualDeductibleOrLimitValue={
              planInfo?.annualOonDeductible || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.specialist")} />
          <DsText variant="body2" text={formatToDollarAmount(planInfo?.specialistVisit || NA)} />
          <DsText
            variant="caption"
            text={planInfo?.specialistVisitLe || NA}
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.exchange")} />
          <DsText variant="body2" text={planInfo?.exchange || NA} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t("planDetails.innOopLimit")}
            annualDeductibleOrLimitValue={
              planInfo?.annualInnOopLimits || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <RxCoverage
            rxCoverageGeneric={planInfo?.rxCoverageGeneric || NA}
            rxCoveragePreferred={planInfo?.rxCoveragePreferred || NA}
            rxCoverageNonPreferred={planInfo?.rxCoverageNonPreferred || NA}
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.metalLevel")} />
          <DsText variant="body2" text={planInfo?.metalLevel || NA} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t("planDetails.oonOopLimit")}
            annualDeductibleOrLimitValue={
              planInfo?.annualOonOopLimits || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t("planDetails.planYear")} />
          <DsText variant="body2" text={planInfo?.availability || NA} />
        </Grid>
      </Grid>
    </Grid>
  );
};


export const MobileDetails = () => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12} className={classes.gridBottomMargin}>
        <MobilePlanInfo />
      </Grid>
      <Grid item xs={12}>
        <MobileBenefitsAndLimits />
      </Grid>
    </Grid>
  );
};

const MobilePlanInfo = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    appStore: { planInfo },
  } = useAppStore();

  return (
    <Grid container className={classes.mobileTableContainer}>
      <Grid container item>
        <Grid item xs className={classes.titleCell}>
          <DsText variant="body2" text={t('planDetails.title1')} />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.state')}/>
          <DsText variant="body2" text={capitalizeWords(planInfo?.planState || NA)} />
          <DsText variant="caption" text={capitalizeWords(planInfo?.planCounty || NA)} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.planType')} />
          <DsText variant="body2" text={planInfo?.planType || NA} />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.exchange')} />
          <DsText variant="body2" text={planInfo?.exchange || NA} />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.metalLevel')} />
          <DsText variant="body2" text={planInfo?.metalLevel || NA} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const MobileBenefitsAndLimits = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    appStore: { planInfo },
  } = useAppStore();

  return (
    <Grid container className={classes.mobileTableContainer}>
      <Grid container item>
        <Grid item xs className={classes.titleCell}>
          <DsText variant="body2" text={t('planDetails.title2')} />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t('planDetails.abbreviated.innDeductible')}
            annualDeductibleOrLimitValue={
              planInfo?.annualInnDeductible || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t('planDetails.abbreviated.oonDeductible')}
            annualDeductibleOrLimitValue={
              planInfo?.annualOonDeductible || NA
            }
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t('planDetails.abbreviated.innOopLimit')}
            annualDeductibleOrLimitValue={
              planInfo?.annualInnOopLimits || NA
            }
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <AnnualDeductibleOrLimit
            title={t('planDetails.abbreviated.oonOopLimit')}
            annualDeductibleOrLimitValue={
              planInfo?.annualOonOopLimits || NA
            }
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.primaryCare')} />
          <DsText variant="body2" text={formatToDollarAmount(planInfo?.primaryCareVisit || NA)} />
          <DsText
            variant="caption"
            text={planInfo?.primaryCareVisitLe || NA}
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline" text={t('planDetails.specialist')} />
          <DsText variant="body2" text={formatToDollarAmount(planInfo?.specialistVisit || NA)} />
          <DsText
            variant="caption"
            text={planInfo?.specialistVisitLe || NA}
          />
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs className={classes.contentCell}>
          <RxCoverage
            isMobile={true}
            rxCoverageGeneric={planInfo?.rxCoverageGeneric || NA}
            rxCoveragePreferred={planInfo?.rxCoveragePreferred || NA}
            rxCoverageNonPreferred={planInfo?.rxCoverageNonPreferred || NA}
          />
        </Grid>
        <Grid item xs className={classes.contentCell}>
          <DsText variant="overline"  text={t('planDetails.planYear')} />
          <DsText variant="body2" text={planInfo?.availability || NA} />
        </Grid>
      </Grid>
    </Grid>
  );
};